import React from "react";
import { useApp } from "../context/AppContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, EmptyState, SectionTitle, Gauge } from "../components/common";
import { Button } from "../components/ui/button";
import { downloadFile } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, CheckCircle2, ArrowRight, FileDown } from "lucide-react";

function Inner() {
  const { datasetId, currentDataset } = useApp();
  const { data, loading } = useFetch(datasetId ? `/datasets/${datasetId}/conformance` : null, { skip: !datasetId });

  if (!datasetId) return <EmptyState icon={ShieldCheck} title="No dataset selected" />;
  if (loading) return <Loading label="Replaying tokens & checking conformance…" />;
  if (!data) return null;

  const maxDev = Math.max(...data.deviations.map((d) => d.count), 1);

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="Conformance Checking" title="Conformance Dashboard" sub="Reference model vs. actual execution"
        right={<Button data-testid="export-conformance-pdf" onClick={async () => {
          try { await downloadFile(`/reports/${datasetId}/pdf?report_type=Conformance Report`, `${currentDataset?.name || "conformance"}.pdf`); toast.success("Report downloaded"); }
          catch { toast.error("Export failed"); }
        }} variant="outline" className="rounded-xl h-11 glass border-white/60"><FileDown className="w-4 h-4 mr-2" />Export PDF</Button>} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <GlassCard className="lg:col-span-3 p-6 flex flex-col items-center justify-center">
          <Gauge value={data.overall_fitness} label="Overall fitness" size={170} color="#06b6d4" />
          <p className="text-xs text-slate-500 text-center mt-3">Average trace fitness against the reference process model.</p>
        </GlassCard>

        <GlassCard className="lg:col-span-4 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Case conformance</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
              <div className="flex-1"><div className="text-2xl font-bold text-slate-900 font-display">{data.conforming_cases}</div><div className="text-xs text-slate-500">Conforming cases</div></div>
              <span className="text-lg font-bold text-emerald-600">{data.conformance_rate}%</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <div className="flex-1"><div className="text-2xl font-bold text-slate-900 font-display">{data.non_conforming_cases}</div><div className="text-xs text-slate-500">Non-conforming cases</div></div>
              <span className="text-lg font-bold text-red-600">{(100 - data.conformance_rate).toFixed(1)}%</span>
            </div>
            <div className="h-3 rounded-full bg-red-200 overflow-hidden flex">
              <div className="h-full bg-emerald-500" style={{ width: `${data.conformance_rate}%` }} />
            </div>
            <div className="text-xs text-slate-500">{data.total_cases} total cases analysed</div>
          </div>
        </GlassCard>

        <GlassCard className="lg:col-span-5 p-6">
          <h3 className="font-bold text-slate-900 mb-2">Reference model (happy path)</h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {(data.reference_model?.happy_path || []).map((a, i, arr) => (
              <React.Fragment key={i}>
                <span className="px-2 py-1 rounded-lg text-xs font-medium bg-cyan-50 border border-cyan-200 text-cyan-800">{a}</span>
                {i < arr.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
              </React.Fragment>
            ))}
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-6">
        <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-amber-500" /><h3 className="font-bold text-slate-900">Deviation heatmap</h3></div>
        {data.deviations.length === 0 ? (
          <p className="text-sm text-slate-500">No deviations detected — the process fully conforms to the reference model.</p>
        ) : (
          <div className="space-y-2">
            {data.deviations.map((d, i) => {
              const intensity = d.count / maxDev;
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white/60" data-testid={`deviation-${i}`}>
                  <div className="flex items-center gap-2 flex-1 text-sm">
                    <span className="font-medium text-slate-700">{d.from}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-red-400" />
                    <span className="font-medium text-slate-700">{d.to}</span>
                    <span className="text-xs text-slate-400 ml-2">{d.type}</span>
                  </div>
                  <div className="w-40 h-6 rounded-lg overflow-hidden bg-slate-100">
                    <div className="h-full rounded-lg flex items-center justify-end px-2 text-[10px] font-bold text-white"
                      style={{ width: `${Math.max(20, intensity * 100)}%`, background: `rgba(239,68,68,${0.4 + intensity * 0.6})` }}>{d.count}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

export default function Conformance() { return <NeedTenant><Inner /></NeedTenant>; }
